import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import StatusBadge from "../shared/StatusBadge";
import RecentOrdersIcon from "./RecentOrdersIcon";
import { OrderRowData } from "../../types";

interface OrdersInvoicesCardProps {
  orders: OrderRowData[];
  showRecentOrdersHeader?: boolean;
  viewAllEnabled?: boolean;
}

const OrdersInvoicesCard: React.FC<OrdersInvoicesCardProps> = ({
  orders,
  showRecentOrdersHeader = true,
  viewAllEnabled = true,
}) => {
  const navigate = useNavigate();
  const { partnerID } = useParams();

  const handleViewAll = () => {
    if (!viewAllEnabled) return;

    if (!showRecentOrdersHeader && partnerID) {
      navigate(`/partner/${partnerID}/dashboard/settings`, {
        state: { selectedTab: "order-history" },
      });
      return;
    }

    navigate("../orders");
  };

  const viewAllButton = (
    <button
      type="button"
      className="orders-invoices-card__view-all"
      onClick={handleViewAll}
      disabled={!viewAllEnabled}
      aria-disabled={!viewAllEnabled}
    >
      View All
      <Iconography icon="chevronRight" size={IconographySizes.Small} />
    </button>
  );

  return (
    <div className="orders-invoices-card">
      <div className="orders-invoices-card__header">
        <h3 className="orders-invoices-card__title">Orders & Invoices</h3>
        {!showRecentOrdersHeader && viewAllButton}
      </div>

      <div className="orders-invoices-card__inner">
        {showRecentOrdersHeader && (
          <div className="orders-invoices-card__inner-header">
            <div className="orders-invoices-card__inner-header-left">
              <span className="orders-invoices-card__icon" aria-hidden>
                <RecentOrdersIcon />
              </span>
              <span className="orders-invoices-card__inner-title">Recent Orders</span>
            </div>
            {viewAllButton}
          </div>
        )}

        <div
          className={`orders-invoices-card__list ${
            !showRecentOrdersHeader
              ? "orders-invoices-card__list--no-header"
              : ""
          }`}
        >
          {orders.length === 0 && (
            <div className="orders-invoices-card__empty">
              No orders or invoices available
            </div>
          )}
          {orders.map((order) => (
            <div key={order.id} className="orders-invoices-card__row">
              <div className="orders-invoices-card__row-top">
                <div className="orders-invoices-card__badges">
                  <span className="orders-invoices-card__id-badge">{order.id}</span>
                  <span className="orders-invoices-card__category-badge">
                    {order.category}
                  </span>
                </div>
                <span className="orders-invoices-card__amount">{order.amount}</span>
              </div>
              <div className="orders-invoices-card__row-middle">
                <p className="orders-invoices-card__description">{order.description}</p>
                <StatusBadge
                  label={order.statusLabel}
                  variant={order.statusVariant}
                />
              </div>
              <span className="orders-invoices-card__date">{order.date}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrdersInvoicesCard;
